import React, { useState, useEffect, useCallback } from 'react';
import {
  Box,
  Typography,
  Button,
  Paper,
  Chip,
  TextField,
  CircularProgress,
  LinearProgress,
  Divider,
} from '@mui/material';
import { Save as SaveIcon, Refresh as RefreshIcon } from '@mui/icons-material';
import { toast } from 'react-toastify';

import { getTenantInfo, updateTenantOrganisation, TenantInfo } from '../../services/tenantAdmin';

/** Tenant-admin organisation profile — used as a tab inside the Settings page.
 *  Only the display name is editable; plan/modules/quota stay super-admin-owned. */
const TenantOrganisationSection: React.FC = () => {
  const [tenantInfo, setTenantInfo] = useState<TenantInfo | null>(null);
  const [orgName, setOrgName] = useState('');
  const [isSavingOrg, setIsSavingOrg] = useState(false);
  const [orgError, setOrgError] = useState<string | null>(null);

  const loadTenantInfo = useCallback(async () => {
    try {
      setOrgError(null);
      const info = await getTenantInfo();
      setTenantInfo(info);
      setOrgName(info.name);
    } catch (error: any) {
      setTenantInfo(null);
      const msg = error.response?.data?.detail || 'Failed to load organisation details';
      setOrgError(msg);
      toast.error(msg);
    }
  }, []);

  useEffect(() => {
    loadTenantInfo();
  }, [loadTenantInfo]);

  const handleSaveOrg = async () => {
    if (!orgName.trim()) {
      toast.error('Organisation name is required');
      return;
    }
    try {
      setIsSavingOrg(true);
      const info = await updateTenantOrganisation(orgName.trim());
      setTenantInfo(info);
      toast.success('Organisation updated');
    } catch (error: any) {
      toast.error(error.response?.data?.detail || 'Failed to update organisation');
    } finally {
      setIsSavingOrg(false);
    }
  };

  const quotaPct = tenantInfo && tenantInfo.monthly_document_limit > 0
    ? Math.min((tenantInfo.monthly_documents_used / tenantInfo.monthly_document_limit) * 100, 100)
    : 0;

  return (
    <Paper variant="outlined" sx={{ p: 3 }}>
      {orgError ? (
        <Box display="flex" flexDirection="column" alignItems="center" gap={2} py={6}>
          <Typography color="error">{orgError}</Typography>
          <Button variant="outlined" startIcon={<RefreshIcon />} onClick={loadTenantInfo}>
            Retry
          </Button>
        </Box>
      ) : !tenantInfo ? (
        <Box display="flex" justifyContent="center" py={6}>
          <CircularProgress />
        </Box>
      ) : (
        <Box display="flex" flexDirection="column" gap={3} maxWidth={560}>
          <Box>
            <Typography variant="h6" fontWeight={600}>
              Organisation profile
            </Typography>
            <Typography variant="body2" color="textSecondary">
              Your plan, modules and quota are managed by the platform administrator.
            </Typography>
          </Box>

          <Box display="flex" gap={2} alignItems="flex-start">
            <TextField
              label="Organisation name"
              value={orgName}
              onChange={(e) => setOrgName(e.target.value)}
              fullWidth
            />
            <Button
              variant="contained"
              startIcon={<SaveIcon />}
              onClick={handleSaveOrg}
              disabled={isSavingOrg || orgName.trim() === tenantInfo.name}
              sx={{ mt: 1, whiteSpace: 'nowrap' }}
            >
              Save
            </Button>
          </Box>

          <Divider />

          <Box display="flex" gap={4} flexWrap="wrap">
            <Box>
              <Typography variant="caption" color="textSecondary">Slug</Typography>
              <Typography variant="body1">{tenantInfo.slug}</Typography>
            </Box>
            <Box>
              <Typography variant="caption" color="textSecondary">Plan</Typography>
              <Typography variant="body1">{tenantInfo.plan_name || tenantInfo.plan_code || '—'}</Typography>
            </Box>
            <Box>
              <Typography variant="caption" color="textSecondary">Status</Typography>
              <Box>
                <Chip
                  label={tenantInfo.status === 'active' ? 'Active' : 'Suspended'}
                  color={tenantInfo.status === 'active' ? 'success' : 'default'}
                  size="small"
                />
              </Box>
            </Box>
          </Box>

          <Box>
            <Box display="flex" justifyContent="space-between" mb={0.5}>
              <Typography variant="body2" color="textSecondary">
                Monthly documents (pooled across the tenant)
              </Typography>
              <Typography variant="body2" color="textSecondary">
                {tenantInfo.monthly_document_limit === -1
                  ? `${tenantInfo.monthly_documents_used.toLocaleString()} (unlimited)`
                  : `${tenantInfo.monthly_documents_used.toLocaleString()} / ${tenantInfo.monthly_document_limit.toLocaleString()}`}
              </Typography>
            </Box>
            {tenantInfo.monthly_document_limit !== -1 && (
              <LinearProgress
                variant="determinate"
                value={quotaPct}
                color={quotaPct > 80 ? 'warning' : 'primary'}
                sx={{ height: 8, borderRadius: 4 }}
              />
            )}
          </Box>
        </Box>
      )}
    </Paper>
  );
};

export default TenantOrganisationSection;
